import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  BookOpen,
  ExternalLink,
  ImageIcon,
  Map as MapIcon,
  Search,
  Settings,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import { getWordsByWeek } from "@/lib/api";
import { googleImageSearchUrl, weblioUrl } from "@/lib/word-links";
import { useAudio } from "@/hooks/useAudio";
import { weekMeta } from "@/lib/course";
import { haptic } from "@/lib/haptic";
import type { Word } from "@/types";

function formatPhonetic(p: string) {
  return p.replace(/'/g, "ˈ").replace(/,/g, "ˌ");
}

function audioMuted() {
  return typeof window !== "undefined" && localStorage.getItem("locomoco_mute_audio") === "1";
}

type PlayingSource = "word" | "example";

function AudioButton({
  active,
  onClick,
  size = "sm",
}: {
  active: boolean;
  onClick: () => void;
  size?: "sm" | "md";
}) {
  const dim = size === "md" ? "w-9 h-9" : "w-8 h-8";
  const icon = size === "md" ? 16 : 14;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${dim} shrink-0 rounded-xl flex items-center justify-center transition-all active:scale-90 ${
        active
          ? "bg-indigo-600 text-white shadow-md shadow-indigo-600/30"
          : "bg-white/5 border border-white/10 text-indigo-400"
      }`}
      aria-label={active ? "音声を停止" : "音声を再生"}
    >
      {active ? <VolumeX size={icon} /> : <Volume2 size={icon} />}
    </button>
  );
}

function WordListItem({
  word,
  index,
  isOpen,
  playingSource,
  onToggle,
  onPlay,
}: {
  word: Word;
  index: number;
  isOpen: boolean;
  playingSource: PlayingSource | null;
  onToggle: () => void;
  onPlay: (source: PlayingSource, text: string) => void;
}) {
  const wordPlaying = playingSource === "word";
  const examplePlaying = playingSource === "example";

  return (
    <motion.article
      layout
      className={`rounded-[1.35rem] border overflow-hidden transition-colors ${
        isOpen
          ? "bg-white/[0.07] border-white/20 shadow-lg shadow-black/20"
          : "bg-white/[0.03] border-white/8"
      }`}
    >
      <div className="w-full px-4 py-3.5 flex items-center gap-3">
        <span className="w-7 text-[11px] font-black text-slate-600 tabular-nums shrink-0">
          {String(index + 1).padStart(2, "0")}
        </span>

        <button type="button" onClick={onToggle} className="flex-1 min-w-0 text-left active:opacity-80">
          <p className="text-lg font-black tracking-tight truncate [font-family:var(--font-heading)]">
            {word.word}
          </p>
          <p className="text-[11px] text-indigo-400/90 truncate [font-family:var(--font-phonetic)]">
            {word.phonetic ? `/${formatPhonetic(word.phonetic)}/` : ""}
          </p>
        </button>

        <AudioButton active={wordPlaying} onClick={() => onPlay("word", word.word)} />

        <button
          type="button"
          onClick={onToggle}
          className="hidden sm:block flex-1 min-w-0 text-left max-w-[28%]"
        >
          <p className="text-sm text-slate-400 truncate">{word.meaning}</p>
        </button>

        <button type="button" onClick={onToggle} className="shrink-0 p-1">
          <Sparkles
            size={14}
            className={`transition-colors ${isOpen ? "text-violet-400" : "text-slate-600"}`}
          />
        </button>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-0 border-t border-white/5">
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xl font-black tracking-tight [font-family:var(--font-heading)]">
                    {word.word}
                  </p>
                  <p className="text-xs text-indigo-400/90 mt-0.5 [font-family:var(--font-phonetic)]">
                    {word.phonetic ? `/${formatPhonetic(word.phonetic)}/` : ""}
                  </p>
                </div>
                <AudioButton
                  active={wordPlaying}
                  size="md"
                  onClick={() => onPlay("word", word.word)}
                />
              </div>

              <p className="text-base font-bold text-white mt-3 leading-snug">{word.meaning}</p>

              <div className="mt-3 p-3 rounded-xl bg-black/25 border border-white/5 flex gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-300 leading-relaxed">{word.example_sentence}</p>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                    {word.example_translation}
                  </p>
                </div>
                <AudioButton
                  active={examplePlaying}
                  size="md"
                  onClick={() => onPlay("example", word.example_sentence)}
                />
              </div>

              <div className="mt-3 flex gap-2">
                <a
                  href={googleImageSearchUrl(word.word)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => haptic(8)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-pink-500/20 to-orange-500/20 border border-pink-500/30 text-pink-200 text-xs font-black active:scale-[0.98] transition-transform"
                >
                  <ImageIcon size={14} />
                  画像検索
                  <ExternalLink size={10} className="opacity-60" />
                </a>
                <a
                  href={weblioUrl(word.word)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => haptic(8)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 text-blue-200 text-xs font-black active:scale-[0.98] transition-transform"
                >
                  <BookOpen size={14} />
                  Weblio
                  <ExternalLink size={10} className="opacity-60" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.article>
  );
}

export default function WeekWords() {
  const navigate = useNavigate();
  const params = useParams();
  const week = Number(params.week);
  const meta = week >= 1 ? weekMeta(week) : null;

  const { speak, stop, isPlaying } = useAudio();

  const [words, setWords] = useState<Word[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const [playing, setPlaying] = useState<{ wordId: string; source: PlayingSource } | null>(null);

  useEffect(() => {
    if (!isPlaying) setPlaying(null);
  }, [isPlaying]);

  useEffect(() => {
    if (!meta) {
      setLoading(false);
      return;
    }
    getWordsByWeek(week).then((data) => {
      setWords(data ?? []);
      setLoading(false);
    });
  }, [week, meta]);

  const wordIndexMap = useMemo(() => new Map(words.map((w, i) => [w.id, i])), [words]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return words;
    return words.filter(
      (w) =>
        w.word.toLowerCase().includes(q) ||
        w.meaning.includes(q) ||
        w.phonetic.toLowerCase().includes(q),
    );
  }, [words, query]);

  const handlePlay = useCallback(
    (wordId: string, source: PlayingSource, text: string) => {
      if (audioMuted()) return;
      haptic(8);

      const isSame = playing?.wordId === wordId && playing?.source === source && isPlaying;
      if (isSame) {
        stop();
        setPlaying(null);
        return;
      }

      setPlaying({ wordId, source });
      speak(text, "en-US");
    },
    [playing, isPlaying, speak, stop],
  );

  const handleToggle = useCallback((wordId: string) => {
    haptic(6);
    setExpanded((prev) => (prev === wordId ? null : wordId));
  }, []);

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#0b0f1a] flex items-center justify-center text-slate-400">
        週が見つかりません
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0f1a] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-[#0b0f1a] text-white min-h-[100dvh] pb-28">
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${meta.accent} opacity-20`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="relative pt-safe px-5 pb-6">
          <button
            onClick={() => {
              haptic(8);
              navigate("/progress");
            }}
            className="flex items-center gap-1.5 text-slate-400 text-sm font-bold mb-5 active:opacity-70"
          >
            <ArrowLeft size={16} />
            マップに戻る
          </button>

          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-black tracking-[0.3em] text-slate-400 mb-1">VOCAB LIST</p>
              <h1 className="text-3xl font-black tracking-tight flex items-center gap-2">
                <span>{meta.emoji}</span>
                {meta.title}
              </h1>
              <p className="text-slate-400 text-sm mt-1">{meta.subtitle}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-4xl font-black tabular-nums leading-none">{words.length}</p>
              <p className="text-[10px] font-bold text-slate-500 tracking-wider mt-0.5">WORDS</p>
            </div>
          </div>

          <div className="mt-5 relative">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="単語・意味・発音で検索"
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl bg-white/5 border border-white/10 text-white placeholder:text-slate-500 text-sm font-medium focus:outline-none focus:border-blue-500/50 focus:bg-white/[0.07] transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="px-4 pt-2.5 space-y-2.5">
        <AnimatePresence mode="popLayout">
          {filtered.length === 0 ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center text-slate-500 py-16 text-sm"
            >
              該当する単語がありません
            </motion.p>
          ) : (
            filtered.map((word, idx) => (
              <motion.div
                key={word.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ delay: Math.min(idx * 0.015, 0.4) }}
              >
                <WordListItem
                  word={word}
                  index={wordIndexMap.get(word.id) ?? idx}
                  isOpen={expanded === word.id}
                  playingSource={playing?.wordId === word.id ? playing.source : null}
                  onToggle={() => handleToggle(word.id)}
                  onPlay={(source, text) => handlePlay(word.id, source, text)}
                />
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      <div className="fixed bottom-0 left-0 right-0 pb-safe px-6 bg-gradient-to-t from-[#0b0f1a] to-transparent pointer-events-none">
        <nav className="max-w-sm mx-auto bg-slate-900/90 backdrop-blur-2xl border border-white/10 rounded-full p-1.5 flex items-center justify-around shadow-2xl pointer-events-auto">
          <button
            onClick={() => {
              haptic(10);
              navigate("/");
            }}
            className="p-3 text-slate-400 hover:text-blue-400 active:scale-90 transition-all"
          >
            <BookOpen size={22} />
          </button>
          <button className="p-3 bg-blue-600 text-white rounded-full shadow-lg shadow-blue-600/30">
            <MapIcon size={22} />
          </button>
          <button
            onClick={() => {
              haptic(10);
              navigate("/settings");
            }}
            className="p-3 text-slate-400 hover:text-blue-400 active:scale-90 transition-all"
          >
            <Settings size={22} />
          </button>
        </nav>
      </div>
    </div>
  );
}
